import type { SupabaseClient, User } from "@supabase/supabase-js";

/**
 * Ensures a row exists in `profiles` for the given auth user (email/password or OAuth).
 * Mirrors client-side logic in AuthPopup — keep in sync when profile defaults change.
 */
export async function ensureProfileForUser(supabase: SupabaseClient, user: User): Promise<void> {
  const { data: existing } = await supabase.from("profiles").select("id").eq("id", user.id).maybeSingle();

  if (existing) return;

  const profileData: {
    id: string;
    email: string;
    full_name?: string | null;
    avatar_url?: string | null;
    phone?: string | null;
    role: string;
    is_admin: boolean;
    onboarding_completed: boolean;
  } = {
    id: user.id,
    email: user.email || "",
    is_admin: false,
    role: "user",
    onboarding_completed: false,
  };

  if (user.user_metadata?.full_name) {
    profileData.full_name = user.user_metadata.full_name as string;
  } else if (user.user_metadata?.name) {
    profileData.full_name = user.user_metadata.name as string;
  } else if (user.email) {
    profileData.full_name = user.email.split("@")[0];
  }

  if (user.user_metadata?.avatar_url || user.user_metadata?.picture) {
    profileData.avatar_url = (user.user_metadata.avatar_url || user.user_metadata.picture) as string;
  }

  if (user.user_metadata?.phone) {
    profileData.phone = user.user_metadata.phone as string;
  }

  const { error: insertError } = await supabase.from("profiles").insert(profileData);
  if (insertError && insertError.code !== "23505") {
    console.error("[ensureProfileForUser] insert", insertError);
    throw insertError;
  }
}
